import { Tag } from 'antd';
import ProductNote, { NoteSm } from 'components/ProductNote';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import QuantityInput from 'containers/QuantityInput';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Language, Product, TrackingAddToCartAddFromType } from 'types/schema';
import { allowDecimalQuantity } from 'utils/allowDecimalQuantity';
import formatMoney from 'utils/formatMoney';
import generateProductSlug from 'utils/generateProductSlug';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { DetailsContainer } from './DetailsContainer';
import { Price } from './Price';
import TagIcon from './Tag.svg';

const Title = styled.div`
  overflow: hidden;
  font-size: ${({ isProductDetail }) => (isProductDetail ? '14px' : '13px')};
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 16px;
  max-height: 35px;
  color: #595959;
  font-weight: 500;
  margin-bottom: 2px;
`;
const SubTitle = styled.p`
  overflow: hidden;
  font-size: 12px;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-height: 14px;
  line-height: 16px;
  font-weight: 500;
  color: #757d73;
  padding: 2px 4px;
  width: fit-content;
  border-radius: 4px;
  background: #f5f5f4;
`;
const Header = styled.h3`
  margin-right: ${({ marginRight }) => marginRight};
  margin-bottom: 8px;
  height: 45px;
  ${media.md`
    padding-right: 0;
  `};
`;

const FooterContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  margin-top: 5px;
  ${({ isHomePage }) =>
    isHomePage &&
    `
    column-gap: 10px;
  `}
`;

const TagExclusiveWrapper = styled.div`
  color: #424242;
  font-size: 12px;
  font-weight: 400;
`;
const TagExclusive = styled.div`
  padding: 4px 8px;
  background: #e6d8bb;
  color: #806e44;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  width: fit-content;
`;

const SaleContact = styled.div`
  border: 1px solid #aaacb2;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
  a {
    color: #595959;
    text-decoration: none;
  }
`;

const addToCartPermission = 'ORDER_PLACE';

const SavingAmountWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0px 8px 0px 6px;
  background: #fff0e5;
  border-radius: 4px;
  gap: 2px;
  width: fit-content;
  line-height: 24px;
  color: #e56910;
  font-size: 12px;
  font-weight: 400;
  margin: 10px 0px;
  @media (max-width: 767px) {
    margin: 0px;
  }
`;

const SavingPrice = styled.span`
  font-weight: 500;
`;

interface IProps {
  product: Product;
  loggedIn: boolean;
  noImage?: boolean;
  permissions: string[];
  onOpenNote: () => void;
  onDeleteNote: () => void;
  addFrom: TrackingAddToCartAddFromType;
  tag?: string | null;
  isHideNote?: boolean;
  isProductDetail?: boolean;
}

const Details: FC<IProps> = (props) => {
  const { loggedIn, noImage, product, permissions, onOpenNote, addFrom, tag, isHideNote, isProductDetail } = props;
  const { name, inStock, uom, note, private: isPrivate, allowPurchase, baseUomPrice, baseUom, savingAmount } = product;
  const lang = localStorage.getItem('lang') || Language.Vi;

  const QuantityComponent = useMemo(() => {
    return isPrivate && !allowPurchase ? (
      <SaleContact>
        <a rel="noopener noreferrer" target="_blank" href="https://zalo.me/2357831519751856428">
          <FormattedMessage {...utilsMessages.talkToSale} />
        </a>
      </SaleContact>
    ) : inStock ? (
      <QuantityInput
        product={product}
        size="small"
        willOverflow
        allowDecimal={allowDecimalQuantity(uom)}
        addFrom={addFrom}
        tag={tag}
      />
    ) : (
      <Tag
        style={{
          background: '#f5f5f4',
          border: 'none',
          borderRadius: 6,
          color: '#BCBFBB',
          fontWeight: 500,
          marginRight: 0,
          padding: '7px 12px',
          float: 'right',
          fontSize: 14,
          width: 110,
          textAlign: 'center',
        }}
      >
        <FormattedMessage {...utilsMessages.outStock} />
      </Tag>
    );
  }, [inStock, product, uom, addFrom, tag, allowPurchase, isPrivate]);

  const { uomInfo, netUom: currNetUom, uom: currUom } = useProductUomInfo(product);

  return (
    <DetailsContainer>
      <Header title={name} isProductDetail={isProductDetail} marginRight={noImage ? '44pt' : ''}>
        <Link to={`/products/${generateProductSlug(name, product.supplierInternalCode, lang)}`}>
          <Title isProductDetail={isProductDetail}>{name}</Title>
        </Link>
        <SubTitle>{uomInfo}</SubTitle>
      </Header>
      {isPrivate && !allowPurchase ? (
        <TagExclusiveWrapper>
          <TagExclusive>
            <FormattedMessage {...utilsMessages.exclusive} />
          </TagExclusive>
          <FormattedMessage {...utilsMessages.onlyForSpecialBusiness} />
        </TagExclusiveWrapper>
      ) : (
        <Price
          product={product}
          uom={currUom}
          baseUom={baseUom || ''}
          baseUomPrice={baseUomPrice || 0}
          netUom={currNetUom}
        />
      )}
      {!!Number(savingAmount) && (
        <SavingAmountWrapper>
          <SVGIcon src={TagIcon} />
          <FormattedMessage {...utilsMessages.savingAmount} />
          <SavingPrice>{formatMoney(savingAmount || 0)}đ</SavingPrice>
        </SavingAmountWrapper>
      )}
      <FooterContainer>
        {loggedIn && note && !isHideNote && <NoteSm>{note}</NoteSm>}
        <FlexContainer isHomePage={window.location.pathname === '/'}>
          {loggedIn && !isHideNote && (
            <ProductNote isShowNote={false} isOnlyShowNoteIcon={true} note={note} onClickOpenNote={onOpenNote} />
          )}
          {(!loggedIn || permissions.indexOf(addToCartPermission) > -1) && QuantityComponent}
        </FlexContainer>
      </FooterContainer>
    </DetailsContainer>
  );
};

export default Details;
